/**
 * Tag helpers for the task board.
 *
 * The authoritative vocabulary and normalisation live in the backend
 * (`util/todo-tags.js`); the kind list and `normalizeTag` are mirrored here so
 * the UI can colour and canonicalise a tag without a round trip. The product
 * areas are deliberately not mirrored — they come from GET /todo/tags.
 */

/** Kind-of-work axis. Mirrors TAG_KINDS in the backend. */
export const TAG_KINDS = ['konfiguration', 'bug', 'feature', 'infrastruktur', 'statistik', 'extern'];

const KIND_SET = new Set(TAG_KINDS);

/** True for a tag on the "Art" axis, false for a product area or a free tag. */
export const isKindTag = (tag) => KIND_SET.has(tag);

/**
 * Only the kind axis carries colour; areas and free tags stay neutral. With
 * both axes in one chip row that is what keeps them apart at a glance.
 */
export const tagChipColor = (tag) => {
  switch (tag) {
    case 'bug':
      return 'error';
    case 'feature':
      return 'info';
    case 'konfiguration':
      return 'warning';
    case 'infrastruktur':
    case 'statistik':
    case 'extern':
      return 'secondary';
    default:
      return 'default';
  }
};

export const MAX_TAGS_PER_TODO = 12;
const MAX_TAG_LENGTH = 40;

/** Canonical tag form — must match normalizeTag in the backend. */
export const normalizeTag = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9äöüß-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_TAG_LENGTH);
};

/** Canonicalises a list, drops empties and duplicates, keeps order. */
export const normalizeTags = (values) => {
  if (!Array.isArray(values)) return [];
  const out = [];
  for (const raw of values) {
    const tag = normalizeTag(raw);
    if (tag && !out.includes(tag)) out.push(tag);
    if (out.length >= MAX_TAGS_PER_TODO) break;
  }
  return out;
};

/** Kind tags first, then everything else alphabetically — stable card layout. */
export const sortTags = (tags) =>
  [...(tags || [])].sort((a, b) => {
    const ka = isKindTag(a);
    const kb = isKindTag(b);
    if (ka !== kb) return ka ? -1 : 1;
    return a.localeCompare(b, 'de');
  });
